import { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { Components } from '@justifi/webcomponents/dist/loader';
import { WebComponentUIContext } from '@weave/schema-gen-ts/dist/schemas/payments-platform/justifi-integration/justifi_integration.pb';
import {
  JustifiTokenizePaymentOrigParams,
  TokenizePaymentMethodErrorCallbackPayloads,
  TokenizePaymentMethodSubmitSuccessEvent,
} from '../types';
import { useJustifiAuthToken } from './use-justifi-auth-token';
import { useJustifiBusinessData } from './use-justifi-business-data';

type JustifiConfig = Omit<JustifiTokenizePaymentOrigParams, 'onSubmit' | 'onError' | 'authToken' | 'accountId'>;

export type UseJustifiTokenizePaymentMethodProps = {
  locationId?: string;
  justifiProps?: JustifiConfig;
  onSubmit?: (data: TokenizePaymentMethodSubmitSuccessEvent) => void;
  onError?: (data: TokenizePaymentMethodErrorCallbackPayloads) => void;
};

export type UseJustifiTokenizePaymentMethod = {
  business: ReturnType<typeof useJustifiBusinessData>['business'];
  authToken: ReturnType<typeof useJustifiAuthToken>['authToken'];
  isLoading: boolean;
  justifiProps: JustifiTokenizePaymentOrigParams;
  elementRef: MutableRefObject<HTMLJustifiTokenizePaymentMethodElement | undefined>;
  tokenizePaymentMethod: () => Promise<
    Awaited<ReturnType<Components.JustifiTokenizePaymentMethod['tokenizePaymentMethod']>> | undefined
  >;
  fillBillingForm: Components.JustifiTokenizePaymentMethod['fillBillingForm'];
};
export function useJustifiTokenizePaymentMethod({
  locationId,
  justifiProps: justifiPropsRaw = {},
  onSubmit: onSubmitCallback,
  onError: onErrorCallback,
}: UseJustifiTokenizePaymentMethodProps): UseJustifiTokenizePaymentMethod {
  const { business, isLoading: isLoadingBusiness } = useJustifiBusinessData({ locationId });

  const justifiConfig: JustifiConfig = {
    iframeOrigin: 'https://components.justifi.ai/',
    hideSubmitButton: true,
    ...justifiPropsRaw,
  };

  const { authToken, isLoading: isLoadingToken } = useJustifiAuthToken({
    locationId,
    businessId: business?.businessId,
    webComponentUiContext: WebComponentUIContext.WEB_COMPONENT_UI_CONTEXT_TOKENIZATION,
  });

  const elementRef = useRef<HTMLJustifiTokenizePaymentMethodElement>();

  const tokenizePaymentMethod = useCallback(async (event?: CustomEvent) => {
    if (elementRef.current) {
      return elementRef.current.tokenizePaymentMethod(event);
    }

    console.warn('JustiFi Tokenize Payment Method Failed: Element not mounted');
    return;
  }, []);

  const fillBillingForm: Components.JustifiTokenizePaymentMethod['fillBillingForm'] = useCallback(async (data) => {
    if (elementRef.current) {
      return elementRef.current.fillBillingForm(data);
    }

    return;
  }, []);

  const onSubmit = useCallback(
    (event: TokenizePaymentMethodSubmitSuccessEvent) => {
      onSubmitCallback?.(event);
    },
    [onSubmitCallback]
  );

  const onError = useCallback(
    (event: TokenizePaymentMethodErrorCallbackPayloads) => {
      console.log('on error event', event);
      onErrorCallback?.(event);
    },
    [onErrorCallback]
  );

  const justifiProps = useMemo<JustifiTokenizePaymentOrigParams>(() => {
    return {
      ...justifiConfig,
      authToken: authToken?.accessToken,
      accountId: business?.subAccountId,
      onSubmit,
      onError,
    };
  }, [justifiConfig, authToken, business, onSubmit, onError]);

  return {
    business,
    authToken,
    isLoading: isLoadingBusiness || isLoadingToken,
    elementRef,
    tokenizePaymentMethod,
    fillBillingForm,
    justifiProps,
  };
}
